import * as React from 'react';
import * as classNames from 'classnames';
import { WTable } from './WTable';
import { WDataColumns } from './WDataColumns';
import { WForm, WFormConfig, WFormData, WFormConfigItem } from '../forms/WFormFramework';
import { StructureTool } from '../forms/helpers/structure_tool';
import DialogWrapper from '../dialog/dialog_wrapper';
import DialogWrapper2 from '../dialog/dialog_wrapper_v2';
import { DataService } from '../../services/data_service';
import { i18n } from '../../i18n/i18n';
import { lazyInject } from '../../services/inversify';
import { WActionButton } from '../elements/WActionButton';
import { DiffTool } from './helpers/diff_tool';
import { formulaProcessData } from '../elements/formula/FormulaTools';
import { TriggerService } from '../trigger/trigger_service';
import { CreateLegend } from './helpers/CreateLegend';
import { RechteService } from '../../services/rechte_service';
import { StructureService } from '../../services/structure_service';
import { ContextService } from '../../services/context_service';
import { filterMethodFulltext } from '../forms/helpers/filter_method_fulltext';
import { generateTableConfig as structureToolGenerateTableConfig } from '../forms/helpers/structure_tool_generate_table_config';

interface WFormsTableWrapperProps extends React.ClassAttributes<any> {
    theme?: string;
    useTheme?: number | boolean;
    containerId?: string;
    formTitel?: string;
    addLabel?: string;                                  // Label für den "add"-Knopf
    tableId?: string;                                   // Table-ID, die immer die Tabelle identifiziert
    entity: string;                                     // key der Entity, ohne "entity_"
    module: string;
    tablePageSize?: number;                             // Anzahl der Datensätze je Tabellen-Seite (optional)
    tableShowPagination?: boolean;
    tableFixedHeader?: boolean;
    showBackup?: any;                                   // Anzeigen der Backup-Action ... TODO: Besser als Custom-Action
    showCompare?: any;                                  // Anzeigen der Diff-Action ... TODO: Besser als Custom-Action
    onShowBackup?: any;                                 // Callback für Backup-Action ... entfällt wenn auf Custom-Action umgestellt
    onShowCompare?: any;                                // Callback für Diff-Action ... entfällt wenn auf Custom-Action umgestellt
    onSaveOriginal?: any;
    actions?: ReadonlyArray<string>;                    // Zum auswählen der Standardactions: edit, delete, add, store
    customActions?: ReadonlyArray<any>;                 // Custom-Actions, also zusätliche Aktions-Knöpfe je Tabellen-Zeile
    hiddenColumns?: ReadonlyArray<string>;              // Keys von Spalten die versteckt werden sollen
    showingColumns?: ReadonlyArray<string>;
    examples?: any;
    hiddenFormFields?: Array<string>;
    externalHeaderElements?: Array<JSX.Element>;        // Elemente, die im Kopf (neben Export und Refresh) angezeigt werden sollen
    externalFormDescriptionElement?: JSX.Element | Array<JSX.Element> | string;
    externalFormElement?: JSX.Element;
    externalFormTopElement?: JSX.Element;
    extraFormElementBefore?: any;
    extraFormElementAfter?: any;
    sortHeaderElements?: Array<string>;
    customColumns?: Array<any>;
    customCssStyle?: any;
    columnWidths?: Array<number>;                       // Spaltenbreite als int-Array, Breite 0 = auto
    showTableHelp?: boolean;
    showColumnsHelp?: boolean;                          // OnLine-Hilfe der Spalten
    showColumnsSortBtn?: boolean;                       // Button Sortierung kenntlich machen
    filterModes?: any;                                  // Filtermodus für Spalten (aktuell gibt es nur "excel"
    filterable?: boolean;                               // Flag: generell Filter ein oder aus
    hideExport?: boolean;
    hideRefresh?: boolean;
    hideFilterReset?: boolean;
    filterList?: Array<any>;                            // Filter list aus QueryGenFilterDialog
    preFilter?: any;                                    // Vor-Filterung der Tabelle UND gleichzeitig Preset fürs Formular
    preFilterServer?: any;                              // Vor-Filterung der Tabelle UND gleichzeitig Preset fürs Formular
    preFilterFunction?: (data: Array<any>) => Array<any>;
    settingsForLoad?: any;                              // Settings direkt an dataService.loadData() übergeben
    data?: any;                                         // Daten zur Anzeige (wenn nicht vorhanden, wird die Entity geladen)
    diffData?: any;                                     // Daten die mit den mit den angezeigten Daten verglichen werden sollen
    backupData?: any;                                   // Daten für Backup anzeigen
    backupDefiniton?: string;                           // backupDefiniton name
    backupEntityName?: string;                           // backup Tabelle name
    backupId?: number;                                  // backup ID
    saveOriginal?: boolean;                             // original data zum herunterladen
    rawData?: any;
    sheetName?: string;
    dataOriginalForSave?: boolean;
    showLegend?: boolean;
    delMissing?: boolean;
    dropFilter?: boolean;
    enableRecover?: boolean;
    usedForDialog?: boolean;                            // Wenn im Dialog, sollte das Flag = true sein, geht um Rendering-Spezifika
    forcedTableKey?: string;                             // Damit wir ein komplettes Neu-Rendern des Tables erzwingen können
    forcedFormKey?: string;
    enableRowCheckbox?: boolean;                        // Auswahlcheckbox je Zeile aktivieren
    enableStorage?: boolean;
    editCondition?: (row: any) => boolean;
    enableTableHistorie?: boolean;
    enableTableStorage?: boolean;
    isLoading?: boolean;                                // Indikator, ob geladen wird (falls Steuerung von aussen)
    actionRechte?: any;
    disableFilter?: boolean;
    disableGlobalFilter?: boolean;                       // Wenn true werden Globale Filter nicht angewendet
    pivotBy?: Array<string>;                            // React Table pivotBy
    expanded?: Array<any>;
    onRowExpand?: (expanded: Array<any>) => void;
    onFilterChange?: (filter: Array<any>, data?: Array<any>) => void;
    aggregate?: any;                                    // React Table aggregate
    defaultSorted?: Array<any>;                         // React Table defaultSorted
    directAddFormular?: boolean;                        // Form für Anlegen direkt anzeigen
    directEditFormularOf?: any;                         // Form für Bearbeiten direkt anzeigen
    inlineFormular?: boolean;
    bundleFilter?: any;                                 // Filter für Bundle, z.B., für Selbst-Referenzierung
    bundleFilterFunc?: (bundle: any) => any;            // Filter für Bundle, z.B., für Selbst-Referenzierung
    hardFormConfig?: any;
    customFormConfig?: any;                             // formConfig ändern, z.B., readonly ermöglichen
    customFormConfigFunc?: (formConfig: WFormConfig, formData: WFormData) => void; // Config nach Daten anpassen
    formPreset?: any;
    refreshStamp?: number;                              // Refresh Data
    formSelectOptionAsRadio?: boolean;
    showFormCopy?: boolean;                              // Um beim Speichern eine Kopie zu erzeugen
    actionsBtnReihenfolg?: Array<any>;
    formLayout?: string;
    formLayout2?: string;
    formAutoSave?: boolean;
    formAutoSaveDisableOnUnmount?: boolean;
    customClass?: string;
    showAsDataColumns?: boolean;
    preCfgWTableFieldGroup?: any;
    validRestriction?: string;
    bundle?: any;
    checkedRows?: any;
    allCheckboxChecked?: boolean;                       // State der all-Checkbox
    exportOptions?: any;
    isManual?: boolean;
    formGroupOrder?: Array<any>;
    formSimpleValidationMsg?: any;
    formDisableValidateField?: Array<string>;
    valueFieldSelectDefaultOptions?: any;
    disableFieldSelectDefaultOptions?: any;
    hideTitel?: boolean;
    globalData?: any;
    additionalFormItems?: Array<WFormConfigItem>;
    skipCloseAction?: boolean;
    callBeforeDelete?: (data: any, callback: () => void) => void;
    callBeforeOnFormSave?: (data: any, callback: () => void) => void;
    callBeforeOnFormAutoSave?: (data: any, callback: () => void) => void;
    onRowSelect?: (row: any, selected: boolean, checkedRows: Array<any>) => void; // ... Funktion zum Aufruf bei select/deselect
    onDeleteLeftoversChange?: (val: boolean) => void;     // Rückgabe des Legenden-Feldes
    isRowSelected?: (row: any) => boolean;                 // ... Funktion zur Prüfung, ob eine Checkbox aktiv sein muss
    isRowSelectable?: (row: any) => boolean;
    tableOrder?: Array<any>;
    onDiffDataProcessed?: (data: any, type: boolean) => void;
    // Wenn gedifft wird, wird diese Callback-Funkt. mit dem Diff-Ergebnis versorgt
    validateField?(key: string, value: any, data: any, onValueChange: (name: string, value: any, doValidate?: boolean) => void): string;
    // Callback für Formularfeld-Validierung
    onDataLoaded?(data: Array<any>): void;
    onAfterAction?(action: string, data?: any): void;
    customFormatter?(field: string, value: any, data?: any, row?: any): any;
    customRechteCheck?(action: string, row: any): boolean;
    customSorter?(a: any, b: any): number;
    onSaveDone?(entityName: string, values: any, bundle: any, saveTid?: number): void;
    onDelDone?(entityName: string, row?: any): void;
    getDefaults?(type: string, entity: string, parameter?: any): any; // Liefert ggf. eine Funktion oder etwas anderes, je nach Typ
    customFilterTranslator?(key: string, value: string): string;
    customTdProps?(state: any, rowInfo: any, column: any): void;
    onFormChange?(data: WFormData): void;               // OnchangeHandler für WForm
    onFormChangeInteractive?(data: WFormData, onValueChange: (name: string, value: any) => void): void;
    filterOptions?(options: Array<any>, key: string, data: any): Array<any>;
    passDataForExport?(data: Array<any>, cols: Array<any>): void;
    summaryCondition?(row: any): boolean;
    showDeleteInSaveDialog?: boolean;
}

interface WFormsTableWrapperState {
    data: Array<any>;                                    // Die Daten, entweder mit reingegeben, oder geladen
    // ACHTUNG: diese Daten sind "resolved"
    showForm: string;                                  // Flag: Formular anzeigen
    currentAction?: string;                             // Die aktuell gewählte Action
    currentRow?: any;                                   // Die aktuell gewählte Zeile
    currentBundle?: any;                                // Das Bundle, welches zu dieser Entity gehört
    currentRowData?: any;                               // Vom Server geladener Datensatz fürs Formular (NICHT "resolved")
    formConfig: WFormConfig;                            // Die Form-Konfiguration
    columns: Array<any>;                                // Die Spalten-Konfiguration
    tablePageSize: number;                              // Anzahl der Datensätze je Tabellen-Seite
    // ACHTUNG:  Gibts in props und auch hier im state,
    // damit wir den Wert manipulieren können
    dualFormContent?: any;
    forLegend?: Array<any>;
    selectedOption?: number;
    filteredData?: any;
    dataForLegend?: Array<any>;
    isLoading: boolean;
    isSaving: boolean;
    showOrigRows?: boolean;                             // Nur für Diff --> Originale Zeilen zeigen (bei geänderten)
    externalRefreshIterator: number;                    // Um einen Table-Redraw zu erzeingen einfach hochzählen
    bundle?: any;
    formValidationMsg?: any;
}

export class WFormsTableWrapper extends React.Component<WFormsTableWrapperProps, WFormsTableWrapperState> {
    constructor(props: any) {
        super(props);

        this.refCallback = this.refCallback.bind(this);
        this.onUpdate = this.onUpdate.bind(this);
        this.onAction = this.onAction.bind(this);
        this.onCloseAction = this.onCloseAction.bind(this);
        this.onSaveDone = this.onSaveDone.bind(this);
        this.onDelDone = this.onDelDone.bind(this);
        this.onFormDelete = this.onFormDelete.bind(this);
        this.onFormSave = this.onFormSave.bind(this);
        this.onFormAutoSave = this.onFormAutoSave.bind(this);
        this.onFormConfigGenerated = this.onFormConfigGenerated.bind(this);
        this.onTableColumnsGenerated = this.onTableColumnsGenerated.bind(this);
        this.setData = this.setData.bind(this);
        this.setFormData = this.setFormData.bind(this);
        this.loadData = this.loadData.bind(this);
        this.getData = this.getData.bind(this);
        this.onChange = this.onChange.bind(this);
        this.notifyAction = this.notifyAction.bind(this);
        this.filterBundle = this.filterBundle.bind(this);
        this.generateTableConfig = this.generateTableConfig.bind(this);
        this.updateFormValidationMsg = this.updateFormValidationMsg.bind(this);

        this.state = {
            data: [],
            dataForLegend: this.props.showLegend && this.props.data ? this.props.data : null,
            showForm: 'none',
            formConfig: null,
            columns: null,
            tablePageSize: this.props.tablePageSize ? this.props.tablePageSize : 10,
            isLoading: false,
            isSaving: false,
            externalRefreshIterator: 0,
            showOrigRows: true,
            bundle: this.props.bundle
        };
        // console.log('BUNDLE_CHECK:', this.props.entity, this.props.bundle);
    }
    private refElement: Element;
    private _mounted = false;

    @lazyInject(DataService) private dataService: DataService;
    @lazyInject(TriggerService) private triggerService: TriggerService;
    @lazyInject(RechteService) private rechteService: RechteService;
    @lazyInject(StructureService) private structureService: StructureService;
    @lazyInject(ContextService) private contextService: ContextService;


    // ************************ STANDARD - FUNKTIONEN ****************************************

    generateTableConfig() {
        // const customFormatter = this.props.customFormatter ? this.props.customFormatter :
        //     this.props.getDefaults ? this.props.getDefaults('customFormatter', this.props.entity) : undefined;
        let customFormatter = undefined;
        if (this.props.customFormatter) {
            customFormatter = this.props.customFormatter;
        } else if (this.props.getDefaults) {
            let parameter;
            if (this.props.entity === 'termine') {
                parameter = this.contextService.get('application_parameter_termine');
            }
            customFormatter = this.props.getDefaults('customFormatter', this.props.entity, parameter);
        }
        const struct_tool = new StructureTool(this.props.module, this.props.globalData);
        if (this.props.hardFormConfig) {     // Form-Config HART vorgeben
            this.onFormConfigGenerated(this.props.hardFormConfig);
        } else {
            struct_tool.generateFormConfig(this.props.entity, this.onFormConfigGenerated);
        }
        structureToolGenerateTableConfig(
            struct_tool,
            this.props.entity,
            this.props.hiddenColumns,
            this.props.showingColumns,
            this.props.examples,
            this.props.filterModes,
            this.getData,
            this.onTableColumnsGenerated,
            customFormatter,
            this.state.bundle,
            this.props.customFilterTranslator,
            this.props.summaryCondition
        );
    }
    componentDidMount() {
        this._mounted = true;
        this.generateTableConfig();
        // Wenn wir die Daten mit reingegeben haben, dann jetzt setzen
        // console.log('WFormasTableWrapper componentDidMount ');
        if (this.props.data || this.props.backupData) {
            const data = this.props.data || this.props.backupData;
            this.setState({ data });
        } else {
            // Wenn es keine übergebenen Daten gibt, versuchen wir zu laden
            this.setState({ isLoading: true }, this.loadData);
        }
        window.addEventListener('resize', () => this.onUpdate());
        // Trigger registrieren
        if (this.props.entity && this.props.entity !== '' && !this.props.data) {
            this.triggerService.register('reload', this.props.entity, this.loadData);
        }
        // Damit gleich das Formular kommt
        if (this.props.directAddFormular) {
            this.onAction('add', {});
        }
        if (this.props.directEditFormularOf) {
            const row = this.props.directEditFormularOf;
            if (row.tid === 0) {
                this.onAction('add', {});
            }
            if (row.tid > 0) {
                this.onAction('edit', { tid: row.tid });
            }
        }
    }
    componentWillUnmount() {
        if (this.props.entity && this.props.entity !== '' && !this.props.data) {
            this.triggerService.unregister('reload', this.props.entity, this.loadData);
        }
    }
    componentDidUpdate(prevProps: any, prevState: any) {
        this.onUpdate();
    }
    onUpdate() {    // Diese Funktion soll die optimale Pagesize "herstellen", funktioniert aber noch nicht optimal ... deshalb ..
        if (true) {
            return;     // ... erstmal nicht machen.
        }
        if (this.refElement && !this.props.usedForDialog) {
            const bbox = this.refElement.getBoundingClientRect();
            const available = window.innerHeight - 30;
            const test = window.outerHeight;
            const sizeEstimate = bbox.top + bbox.height;
            // console.log(available + ' --> ' + sizeEstimate + ' -- ' + this.state.tablePageSize);
            if (sizeEstimate > available && this.state.tablePageSize > 3) {
                const tablePageSize = this.state.tablePageSize - 1;
                const data = this.state.data;
                // window.setTimeout(() => {this.setState({data, tablePageSize}); }, 1);
                this.setState({ data, tablePageSize });
            } else
                if (sizeEstimate < available - 30 && this.state.tablePageSize < 30) {
                    const tablePageSize = this.state.tablePageSize + 1;
                    const data = this.state.data;
                    // window.setTimeout(() => {this.setState({data, tablePageSize}); }, 1);
                    this.setState({ data, tablePageSize });
                }
        }
    }
    UNSAFE_componentWillMount() {
        this._mounted = false;
        window.removeEventListener('resize', () => this.onUpdate());
    }

    UNSAFE_componentWillReceiveProps(props: any) {
        if (props.data || props.backupData) {
            const data = props.data || props.backupData;
            this.setState({ data });
        }
        if (props.refreshStamp !== this.props.refreshStamp) {
            if (this.props.entity && this.props.entity !== '' && !this.props.data) {
                this.setState({ isLoading: true }, this.loadData);
            }
        }
        if (props.bundleFilterFunc && this.state.currentBundle && props.bundleFilterFunc !== this.props.bundleFilterFunc) {
            const currentBundle = props.bundleFilterFunc(this.state.currentBundle);
            this.setState({ currentBundle });
        }
    }

    loadData(callback?: (result: ReadonlyArray<any>) => void) {
        // console.log('WFormsTableWrapper loadData');
        const struct_tool = new StructureTool(this.props.module, this.props.globalData);
        const struct = struct_tool.getStructure(this.props.module, this.props.entity);
        if (struct && struct.SRC && struct.SRC.type === 'CALL') {
            const callName = struct.SRC.callName;
            if (callName) {
                const serverFilter = this.props.preFilterServer ? this.props.preFilterServer : {};
                this.dataService.loadDataForModulCustom(callName, this.props.entity, serverFilter,
                    {
                        resolve: true,
                        ... this.props.settingsForLoad
                    },
                    this.props.module,
                    (result: ReadonlyArray<any>) => {
                        this.setData(result);
                        if (typeof callback === 'function') {
                            callback(result);
                        }
                    });
                return;
            }
        }
        // Wenn keine spezielle Lade-Methode, immer Standard
        this.dataService.loadDataForModul(
            this.props.entity,
            (this.props.preFilterServer ? this.props.preFilterServer : {}),
            {
                resolve: true,
                bundle: true,
                ... this.props.settingsForLoad
            },
            this.props.module,
            (result: any) => {
                this.setData(result);
                this.notifyAction('loadDataDone', result[this.props.entity]);
                if (typeof callback === 'function') {
                    callback(result);
                }
            }
        );

    }
    render() {
        return (
            <div
                ref={this.refCallback}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
            >
                {!this.props.directEditFormularOf && this.renderTable()}
                {this.renderForm()}
            </div>
        );
    }

    // ************************ REF - CALLBACK ****************************************
    refCallback(e: Element) {
        this.refElement = e;
    }

    // ************************ EVENT - EMPFÄNGER ****************************************


    public onFormConfigGenerated(formConfig: WFormConfig) {
        if (!formConfig) {
            return;
        }
        if (this.props.customFormConfig) {
            const cf = this.props.customFormConfig;
            Object.keys(cf).forEach((key: string) => {
                const idx = formConfig.items.findIndex((it: any) => (it.key || it.id) === key);
                if (idx >= 0) {
                    if (cf[key].label) {
                        formConfig.items[idx].label = cf[key].label;
                    }
                    if (typeof cf[key].autofill === 'boolean') {
                        formConfig.items[idx].autofill = cf[key].autofill;
                    }
                    if (typeof cf[key].readonly === 'boolean') {
                        formConfig.items[idx].readonly = cf[key].readonly;
                    }
                    if (typeof cf[key].required === 'boolean') {
                        formConfig.items[idx].required = cf[key].required;
                    }
                    if (typeof cf[key].hidden === 'boolean') {
                        formConfig.items[idx].hidden = cf[key].hidden;
                    }
                    if (typeof cf[key].hidden_input === 'boolean') {
                        formConfig.items[idx].hidden_input = cf[key].hidden_input;
                    }
                    if (typeof cf[key].radio === 'boolean') {
                        formConfig.items[idx].radio = cf[key].radio;
                    }
                    if (typeof cf[key].disable_default === 'boolean') {
                        formConfig.items[idx].disable_default = cf[key].disable_default;
                    }
                    if (cf[key].hidden_msg) {
                        formConfig.items[idx].hidden_msg = cf[key].hidden_msg;
                    }
                    if (cf[key].decimals) {
                        formConfig.items[idx].decimals = cf[key].decimals;
                    }
                    if (cf[key].placeholder) {
                        formConfig.items[idx].placeholder = cf[key].placeholder;
                    }
                    if (cf[key].typ) {
                        formConfig.items[idx].typ = cf[key].typ;
                    }
                    if (cf[key].format) {
                        formConfig.items[idx].format = cf[key].format;
                    }
                    if (cf[key].custom_component) {
                        formConfig.items[idx].custom_component = cf[key].custom_component;
                    }
                    if (cf[key].event_handler) {
                        formConfig.items[idx].event_handler = cf[key].event_handler;
                    }
                    if (cf[key].bundle_output) {
                        formConfig.items[idx].bundle_output = cf[key].bundle_output;
                    }
                    if (cf[key].bundle_text) {
                        formConfig.items[idx].bundle_text = cf[key].bundle_text;
                    }
                    if (cf[key].validMsgStyle) {
                        formConfig.items[idx].validMsgStyle = cf[key].validMsgStyle;
                    }
                }
            });
        }
        if (this.props.additionalFormItems) {
            formConfig.items = [...formConfig.items, ...this.props.additionalFormItems];
        }
        this.setState({ formConfig });
    }
    public onTableColumnsGenerated(columns: Array<any>) {
        // console.log('Columns' , columns);
        this.setState({ columns });
    }
    onFormDelete(data: WFormData): void {
        const values = data.getAllValues();
        const callback = (result: ReadonlyArray<any>, success?: boolean): void => {
            if (this.props.onDelDone) {
                this.props.onDelDone(this.props.entity, values);
            }
            this.onDelDone(); // TODO: Brauchen wir das, wenn wir im Formular-Only-Modus sind?
            if (this.props.directEditFormularOf && !this.props.skipCloseAction) {
                this.onCloseAction();
            }
        };
        if (values) {
            this.dataService.delData(
                this.props.entity,
                this.props.module,
                { tid: values.tid },
                {},
                callback
            );
        }
    }
    onFormSave(data: WFormData): void {
        if (this.state.isSaving) {
            // Vermeide doppeltes Speichern bei langsamen Netzwerkverbindungen
            // alert("Der Datensatz wird gespeichert.");
            return;
        }
        const entityName = this.props.entity;
        const tableId = this.props.tableId;
        const values = data.getAllValues();
        const enableTableHistorie = this.props.enableTableHistorie;
        const callback = (suc: boolean, saveTid: number) => {
            if (this.props.onSaveDone) {
                this.props.onSaveDone(entityName, values, this.state.bundle, saveTid);
            }
            this.onSaveDone(); // TODO: Brauchen wir das, wenn wir im Formular-Only-Modus sind?
            if (this.props.directEditFormularOf && !this.props.skipCloseAction) {
                this.onCloseAction();
            }
        };
        if (this.props.isManual) {
            // Wenn manuell, dann gleich onSaveDone aufrufen
            if (this.props.onSaveDone) {
                this.props.onSaveDone(entityName, values, this.state.bundle, 0);
            }
            if (!this.props.skipCloseAction) {
                this.onCloseAction();
            }
        } else {
            this.setState({ isSaving: true },
                () => this.dataService.saveData(
                    entityName, values, tableId, enableTableHistorie, callback, this.props.module
                )
            );
        }
    }
    onFormAutoSave(data: WFormData, onWFormUnmount?: boolean): void {
        const entityName = this.props.entity;
        const tableId = this.props.tableId;
        const values = data.getAllValues();
        const enableTableHistorie = this.props.enableTableHistorie;
        const doSave = () => this.dataService.saveData(entityName, values, tableId, enableTableHistorie,
            (suc: boolean, saveTid: number) => {
                // console.log(suc, saveTid);
                this.setState({ isSaving: false });
                if (this.props.onSaveDone) {
                    this.props.onSaveDone(entityName, values, this.state.bundle, saveTid);
                }
            },
            this.props.module);
        if (onWFormUnmount) {
            // Beim Unmont muss die AutoSaveFunktion sofort getriggert werden.
            doSave();
        } else {
            this.setState({ isSaving: true }, doSave);
        }
    }
    onAction(action: string, row: any) {
        // console.log('onAction - row', row);
        // console.log('WFormsTableWrapper onAction ' + action, row);
        const that = this;
        let currentAction = action;
        const currentRow = row;
        let showForm = 'mono';
        if (action === 'edit' || action === 'view') {
            if (this.props.isManual) {
                this.setState({ currentAction, currentRow: row, showForm });
                const dataIn: any = {};
                dataIn[this.props.entity] = [row];
                dataIn['bundle'] = [];
                this.setFormData(dataIn, row);
            } else {
                this.dataService.loadDataForModul(
                    this.props.entity, { tid: row.tid },
                    {
                        resolve: false,
                        bundle: true,
                        ... this.props.settingsForLoad
                    },
                    this.props.module, (dataIn: any) => this.setFormData(dataIn, row)
                );
                if (this.props.editCondition && !this.props.editCondition(row)) {
                    currentAction = 'view';
                }
                this.setState({ currentAction, currentRow, showForm });
            }
        } else if (action === 'copy') {
            this.dataService.loadDataForModul(
                this.props.entity, { tid: row.tid },
                {
                    resolve: false,
                    bundle: true,
                    ... this.props.settingsForLoad
                },
                this.props.module, this.setFormData
            );
            this.setState({ currentAction, currentRow, showForm });
        } else if (action === 'add') {
            if (this.props.isManual) {
                const dataIn: any = {};
                dataIn[this.props.entity] = [];
                dataIn['bundle'] = [];
                // console.log('datIn', dataIn);
                this.setFormData(dataIn, { tid: 0 });
                this.setState({ currentAction, currentRow, showForm });
            } else {
                showForm = 'mono';
                this.dataService.loadDataForModul(this.props.entity, { tid: -1 },
                    {
                        resolve: false,
                        bundle: true,
                        ... this.props.settingsForLoad
                    },
                    this.props.module, (dataIn: any) => this.setFormData(dataIn, row));
                this.setState({ currentAction, currentRow, showForm });
            }
        } else if (action === 'delete') {
            const doDelete = () => {
                if (this.props.isManual) {
                    if (this.props.onDelDone) {
                        this.props.onDelDone(this.props.entity, row);
                    }
                } else {
                    if (window.confirm('Datensatz wirklich löschen?')) {
                        this.dataService.delData(
                            this.props.entity,
                            this.props.module,
                            { tid: row.tid },
                            {},
                            this.onDelDone
                        );
                    }
                }
            };
            if (this.props.callBeforeDelete) {
                this.props.callBeforeDelete(row, doDelete);
            } else {
                doDelete();
            }
        } else if (action === 'reload') {
            this.setState({ isLoading: true }, this.loadData);
        } else if (action === 'export') {
            alert('Diese Funktion steht gegenwärtig nicht zur Verfügung.');
        } else if (action === 'recover') {
            this.dataService.recoverBackup(this.props.entity, this.props.module,
                this.props.backupId, this.props.backupEntityName, (newData: any, suc: boolean) => {
                    const newBackupData = newData[this.props.entity];
                    this.dataService.saveBackupForModul(this.props.entity, 'core', this.props.backupDefiniton,
                        newBackupData, this.props.backupEntityName, (backupData: string, succes: boolean) => {
                            const backupDataForImport = JSON.parse(backupData);
                            const logWrite = {
                                definition: this.props.module,
                                schnittstellen: this.props.entity,
                                user: 'user',
                                aktion: 'backup recover',
                                stamp: Date.now(),
                                backup_path: backupDataForImport.backupPath,
                                backup_id: parseInt(backupDataForImport.backupTid, 10)
                            };
                            this.dataService.saveData('import_log', logWrite, null, null, () => this.loadData());
                        });
                });
        }
    }

    notifyAction(action: string, data?: any) {
        if (this.props.onAfterAction) {
            this.props.onAfterAction(action, data);
        }
    }
    onCloseAction() {
        const action = this.state.currentAction || null;
        if (['add', 'edit'].indexOf(action) !== -1) {
            this.notifyAction(`closeForm${action[0].toUpperCase() + action.slice(1)}`);
        }
        // Form wieder deaktivieren und alle temporären Daten entfernen
        const currentAction: string = null;
        const currentRow: any = null;
        const currentRowData: any = null;
        const showForm = 'none';
        this.setState({ currentAction, currentRow, currentRowData, showForm });
        if (this.props.directEditFormularOf && this.props.onAfterAction) {
            this.props.onAfterAction('closeForm');
        }
    }
    onSaveDone() {
        this.setState({ isSaving: false },
            () => {
                this.notifyAction('save');
                this.loadData(() => this.notifyAction('saveDone'));
                if (!this.props.skipCloseAction) {
                    this.onCloseAction();
                }
            });
    }
    onDelDone() {
        if (this.props.onDelDone) {
            this.props.onDelDone(this.props.entity);
        }
        this.notifyAction('del');
        this.loadData(() => this.notifyAction('delDone'));
    }
    // ************************ DATEN - Abruf für die Filter   ****************************

    public getData() {
        return this.state.data;
    }
    // ************************ DATEN - CALLBACKS  ****************************************

    setFormData(dataIn: any, dataRow?: any) {
        // console.log('setFormData', dataIn, dataRow);
        if (!dataIn || !dataIn[this.props.entity]) {
            alert('Datensatz konnte nicht geladen werden!');
            this.onCloseAction();
            return;
        }
        let bundleBase = dataIn['bundle'];
        if (!bundleBase && this.state.bundle) {
            bundleBase = this.state.bundle;
        }
        if (this.props.bundle) {
            Object.keys(this.props.bundle).forEach((k: string) => {
                if (!Array.isArray(bundleBase[k]) && Array.isArray(this.props.bundle[k])) {
                    bundleBase[k] = this.props.bundle[k];
                }
            });
        }
        const currentBundle = this.filterBundle(bundleBase);
        this.populateBundle(currentBundle);
        let currentRowData: any = { tid: 0 }; // Wir erwarten hier nur exakt einen Datensatz
        if (dataIn[this.props.entity].length === 0) {
            // Neuer Datensatz
        } else {
            currentRowData = dataIn[this.props.entity][0]; // Wir erwarten hier nur exakt einen Datensatz
            if (this.state.currentAction === 'copy') {
                currentRowData.tid = 0;
            }
        }
        if (this.props.directEditFormularOf) {
            const rowPreset = { ...this.props.directEditFormularOf };
            delete rowPreset.tid;
            currentRowData = { ...currentRowData, ...rowPreset };
        }
        const action = this.state.currentAction || null;
        if (['add', 'edit'].indexOf(action) !== -1) {
            this.notifyAction(`openForm${action[0].toUpperCase() + action.slice(1)}`, currentRowData);
        }
        if (this._mounted) {
            this.setState({ currentRowData, currentBundle });
        }
    }

    filterBundle(bundle: any) {
        // console.log(bundle, this.props.bundleFilter);
        if (this.props.bundleFilter) {
            const filter = this.props.bundleFilter;
            for (const attr of Object.keys(filter)) {
                for (const feld of Object.keys(filter[attr])) {
                    const val = filter[attr][feld];
                    if (val && Array.isArray(bundle[attr])) {
                        if (Array.isArray(val)) {
                            bundle[attr] = bundle[attr].filter((item: any) => {
                                if (item[feld]) {
                                    return val.indexOf(item[feld]) !== -1;
                                }
                            });
                        } else {
                            bundle[attr] = bundle[attr].filter((item: any) => {
                                if (item[feld]) {
                                    return item[feld] === val;
                                }
                            });
                        }
                    }
                }
            }
        }
        if (this.props.bundleFilterFunc) {
            this.props.bundleFilterFunc(bundle);
        }
        return bundle;
    }

    populateBundle(bundle: any) {
        if (bundle && bundle.entity) {
            const entities = this.structureService.getStructureNames(this.props.module, true);
            bundle.entity = entities.map((ent: any) => { return { name: ent.name, entity: ent.key }; });
        }
    }
    resolveInternal(data: Array<any>) {
        const entities = this.structureService.getStructureNames(this.props.module, true);
        data.forEach((item: any) => {
            if (item.entity) {
                item.entity_orig = item.entity;
                const found_ent = entities.find((ent: any) => ent.key === item.entity);
                if (found_ent) {
                    item.entity = found_ent.name;
                }
            }
        });
    }
    setData(dataIn: any) {
        if (!this._mounted) {
            // Wir verhindern nur, das Ladeprozesse noch auf eine bereits ausgeblendete Tabelle wirken
            return;
        }
        if (dataIn.bundle && Object.keys(dataIn.bundle).length > 0) {
            this.setState({ bundle: dataIn.bundle }, () => {
                this.generateTableConfig();
            });
        }
        // console.log('TableWrapper setData for ' + this.props.entity);
        // console.log(dataIn);
        // let data = dataIn[this.props.entity];
        // data = Array.isArray(data) ? data : [];
        // const struct_tool = new StructureTool(this.props.module);
        // const struct = struct_tool.getStructure(this.props.module, this.props.entity);
        this.structureService.getStructure(this.props.module, (result: ReadonlyArray<any>): void => {
            let data = dataIn[this.props.entity];
            data = Array.isArray(data) ? data : [];
            const struct = this.structureService.getStructureByKey(this.props.module, this.props.entity);
            if (!struct) {
                console.log('Missing Structure: ', this.props.entity);
                return;
            }
            data = this.props.backupData ? this.props.backupData :
                formulaProcessData(struct.ATTS, data, 'pre', {});
            // Hier gibts nur pre, da es keine Aggregation gibt
            // es geht hier nur um Formeln aus der Struktur, nicht QueryGen/FormelEditor
            if (this.props.onDataLoaded) {
                this.props.onDataLoaded(data);
            }
            // console.log(data);
            if (data === null) {
                data = [];
            }
            if ((this.props.showBackup || this.props.showCompare) && !data.backup_id) {
                for (let i = 0; i < data.length; i++) {
                    data[i].backup_id = data[i].tid;
                }
            }
            this.resolveInternal(data);

            if (this.props.diffData) {
                const dt = new DiffTool(this.props.module);
                data = dt.mergeData(this.props.entity, data, this.props.diffData);
            }
            this.setState({ data, isLoading: false }, () => {
                if (this.props.showLegend) {
                    this.setState({ dataForLegend: data }, () => this.refilter(this.state.selectedOption));
                }
            });

            if (this.props.onDiffDataProcessed) {
                const dataProblem = data.filter((row: any) => DiffTool.errorRow(row));
                this.props.onDiffDataProcessed(dataProblem, true);
                this.props.onDiffDataProcessed(data, false);
            }
        }, false);
    }
    receiveDualFormContent(jsx: any) {
        if (jsx === 'noContentAvailable') {
            this.setState({ showForm: 'mono', dualFormContent: jsx });
        } else {
            const dualFormContent = jsx;
            this.setState({ dualFormContent });
        }

    }

    onChange(event: any) {
        const selectedOption = event;
        this.refilter(selectedOption);
    }

    refilter(selectedOption: number) {
        const targetData = this.state.dataForLegend;
        selectedOption = selectedOption ? selectedOption : 0;
        const filteredData = this.filterData(targetData, selectedOption);
        this.setState({ selectedOption, filteredData });
    }

    filterData(dataIn: Array<any>, selectedOption: number) {
        const dataOut: Array<any> = [];
        for (let i = 0; i < dataIn.length; i++) {
            // Wenn der Status == dem gewählten ist
            // oder es keinen Status gibt und "unverändert" gewählt ist
            // oder wenn der Status "geändert orig" ist und "geändert" gewählt ist
            if ((dataIn[i].hasOwnProperty('internal') && (dataIn[i].internal._diffState === selectedOption ||
                (selectedOption === 0 && dataIn[i].internal._diffState !== 8))) ||
                (dataIn[i].hasOwnProperty('internal') && dataIn[i].internal._diffState === 8
                    && this.state.showOrigRows && selectedOption < 2) ||
                (!dataIn[i].hasOwnProperty('internal') && (selectedOption === 4 || selectedOption === 0))) {
                dataOut.push(dataIn[i]);
            }
        }
        return dataOut;
    }

    // ************************ RENDER - FUNKTIONEN  ****************************************
    private renderForm() {
        if (this.state.showForm !== 'none') {
            const { customCssStyle, customFormConfigFunc } = this.props;
            const { formConfig, currentRowData } = this.state;

            // Formdata-Object erzeugen
            const formData = new WFormData();
            if (currentRowData) {
                formData.setAllValues(currentRowData);
            }

            if (typeof customFormConfigFunc === 'function') {
                customFormConfigFunc(formConfig, formData);
            }

            const form = <WForm
                containerId={this.props.containerId}
                useTheme={this.props.useTheme}
                module={this.props.module}
                entity={this.props.entity}
                layout={this.props.formLayout ? this.props.formLayout : 'sidebysideWIPS'}
                layout2={this.props.formLayout2}
                titel={this.props.formTitel || this.getFormTitel()}
                dataIn={formData}
                // preset={this.props.preFilter || this.props.preFilterServer}
                preset={{ ...(this.props.preFilter || this.props.preFilterServer), ...this.props.formPreset }}
                bundle={this.state.currentBundle}
                config={formConfig}
                onFormDelete={this.onFormDelete}
                onFormSave={this.onFormSave}
                formAutoSaveDisableOnUnmount={this.props.formAutoSaveDisableOnUnmount}
                onFormAutoSave={this.props.formAutoSave ? this.onFormAutoSave : null}
                onFormCancel={this.onCloseAction}
                onFormChange={this.props.onFormChange}
                onFormChangeInteractive={this.props.onFormChangeInteractive}
                validateField={this.props.validateField}
                readOnly={this.state.currentAction === 'view'}
                saving={this.state.isSaving}
                customCssStyle={customCssStyle}
                selectOptionAsRadio={this.props.formSelectOptionAsRadio}
                showCopy={this.props.showFormCopy}
                showDeleteInSaveDialog={this.props.showDeleteInSaveDialog}
                validRestriction={this.props.validRestriction}
                hiddenFormFields={this.props.hiddenFormFields}
                disableValidateField={this.props.formDisableValidateField}
                valueFieldSelectDefaultOptions={this.props.valueFieldSelectDefaultOptions}
                disableFieldSelectDefaultOptions={this.props.disableFieldSelectDefaultOptions}
                hideTitel={this.props.hideTitel}
                filterOptions={this.props.filterOptions}
                showFieldsHelp={true || this.props.showColumnsHelp} // Eigenschaft default aktiviert für E-Tool
                hideSave={this.props.formAutoSave}
                groupOrder={this.props.formGroupOrder}
                simpleValidationMsg={this.props.formSimpleValidationMsg}
                callBeforeOnFormSave={this.props.callBeforeOnFormSave}
                callBeforeOnFormAutoSave={this.props.callBeforeOnFormAutoSave}
                externalDescriptionElement={this.props.externalFormDescriptionElement}
                externalElement={this.props.externalFormElement}
                extraElementBefore={this.props.extraFormElementBefore}
                extraElementAfter={this.props.extraFormElementAfter}
                globalData={this.props.globalData}
                key={this.props.forcedFormKey ? this.props.forcedFormKey : undefined}
                externalTopElement={this.props.externalFormTopElement}
                onValidationStateChange={this.updateFormValidationMsg}
            />;

            let dialog = <DialogWrapper
                width={510}
                height={null}
                onAfterClose={this.onCloseAction}
            >
                {this.state.currentRowData ?
                    form
                    :
                    'Lade Datensatz ...'}
            </DialogWrapper>;

            if (this.props.formLayout === 'flowV2') {
                const formValidationMsg = this.state.formValidationMsg || {};
                const hasValidMsg: boolean = Object.keys(formValidationMsg).length > 0;
                // 600px Eingabefeld, 300px für die Fehleranzeige, 50px für Padding.
                // Siehe WForm.tsx, Zeilen um this.renderAssistentValidMsg(item).
                const formWidth: number = customCssStyle && customCssStyle.formDialogWrapper && customCssStyle.formDialogWrapper.width > 0 ?
                    customCssStyle.formDialogWrapper.width : 600;
                const formHeight: number = customCssStyle && customCssStyle.formDialogWrapper && customCssStyle.formDialogWrapper.height > 0 ?
                    customCssStyle.formDialogWrapper.height : null;
                const dialogWidth: number = formWidth + (hasValidMsg ? WForm.maxValidMsgWidth + 50 : 0);

                dialog = <DialogWrapper2
                    containerId={this.props.containerId}
                    width={-1} // --> wird zu "auto"
                    onAfterClose={this.onCloseAction}
                >
                    {this.state.currentRowData ?
                        <div
                            style={{
                                backgroundColor: 'var(--color-wform-background)',
                                width: dialogWidth,
                                height: formHeight,
                                overflowX: 'hidden', // Wichtig
                                overflowY: 'auto', // Wichtig
                                borderRadius: '0 0 20px 20px'
                            }}
                        >
                            <div
                                style={{ width: formWidth }}
                            >
                                {form}
                            </div>
                        </div>
                        :
                        'Lade Datensatz ...'}
                </DialogWrapper2>;
            }

            if (this.props.inlineFormular) {
                return <div>{this.state.currentRowData ? form : 'Lade Datensatz ...'}</div>;
            } else {
                return <div className={this.props.customClass}>{dialog}</div>;
            }
        } else {
            return '';
        }
    }

    private updateFormValidationMsg(formValidationMsg: any) {
        if (JSON.stringify(this.state.formValidationMsg) !== JSON.stringify(formValidationMsg)) {
            this.setState({ formValidationMsg });
        }
    }

    private getFormTitel() {
        // console.log(this.state.formConfig);
        const name = this.state.formConfig && this.state.formConfig.name ? this.state.formConfig.name : 'Datensatz';
        let verb = '';

        if (this.state.currentAction === 'add') {
            verb = 'erstellen';
        } else if (this.state.currentAction === 'copy') {
            verb = 'kopieren';
        } else {
            verb = 'bearbeiten';
        }

        return `${name}${verb ? ` ${i18n(verb, 'sys')}` : ''}`;
    }
    private getTopBarElements() {
        const { theme } = this.props;
        const arrElements: Array<JSX.Element> = [];
        const hasAddRight = !this.props.actionRechte || !this.props.actionRechte.add ||
            this.props.actionRechte.add === '' ||
            this.rechteService.hasRecht(this.props.actionRechte.add);

        if (this.props.actions && this.props.actions.indexOf('add') > -1 && hasAddRight) {
            const addButton =
                <WActionButton
                    style={theme ? `theme_${theme}` : 'theme'}
                    label='Erstellen'
                    action='add'
                    key='action_add'
                    insideText={this.props.addLabel ? this.props.addLabel : i18n('Datensatz erfassen', 'sys')}
                    onAction={(action: string) => this.onAction(action, null)}
                    tooltip={i18n('Datensatz anlegen', 'sys')}
                    styleProps={{ fontWeight: 'bold' }}
                />;
            arrElements.push(addButton);
        }
        if (this.props.actions && this.props.actions.indexOf('backup') > -1 && this.props.diffData && this.props.enableRecover) {
            const backupButton =
                <WActionButton
                    style={theme ? `theme_${theme}` : 'theme'}
                    label={i18n('Wiederherstellen', 'sys')}
                    action='recover'
                    key={'action_recover'}
                    onAction={(action: string) => this.onAction(action, null)}
                    tooltip={i18n('Wiederherstellen', 'sys')}
                />;
            arrElements.push(backupButton);
        }
        const reloadButton =
            <WActionButton
                style={theme ? `theme_${theme}` : 'theme'}
                label={i18n('Erneut laden', 'sys')}
                action='reload'
                key='action_reload'
                onAction={(action: string) => this.onAction(action, null)}
                tooltip={i18n('Neu laden', 'sys')}
                place='bottom'
            />;
        if (this.props.entity && !this.props.data) {
            arrElements.push(reloadButton);
        }
        if (this.props.externalHeaderElements) {
            return arrElements.concat(this.props.externalHeaderElements);
        } else {
            return arrElements;
        }
    }

    private renderTable() {
        let data = this.state.data;
        // console.log('RenderData: ', data);
        if (this.props.preFilterFunction) {
            data = this.props.preFilterFunction(data);
        }
        if (this.props.preFilter) {
            const preFilter = this.props.preFilter;
            const filterKeys = Object.keys(preFilter);
            data = data.filter((row: any) => {
                let match = true;
                filterKeys.forEach((k: any) => {
                    if (row[k + '_orig']) {
                        if (preFilter[k] + '' !== row[k + '_orig'] + '') {
                            match = false;
                        }
                    } else
                        if (preFilter[k] !== row[k]) {
                            match = false;
                        }
                });
                return match;
            });
        }
        // console.log('RenderData2 : ', data);

        if (!this.state.columns || !this.state.formConfig) {
            return 'Lade Struktur ...';
        }
        // console.log('WFormsTableWrapper Render Table  ...');
        // console.log(this.props.tableId);
        // console.log(this.props.forcedTableKey);
        // console.log(this.state.columns);
        // console.log(this.props.customActions);
        // Wir checken potentielle Rechte

        const hasDeleteRight = !this.props.actionRechte || !this.props.actionRechte.delete ||
            this.props.actionRechte.delete === '' ||
            this.rechteService.hasRecht(this.props.actionRechte.delete);
        const hasEditRight = !this.props.actionRechte || !this.props.actionRechte.edit ||
            this.props.actionRechte.edit === '' ||
            this.rechteService.hasRecht(this.props.actionRechte.edit);
        const hasCopyRight = !this.props.actionRechte || !this.props.actionRechte.edit ||
            this.props.actionRechte.edit === '' ||
            this.rechteService.hasRecht(this.props.actionRechte.edit);


        const showEdit = this.props.actions && this.props.actions.indexOf('edit') !== -1 &&
            this.state.formConfig !== null && hasEditRight;
        const showDelete = this.props.actions && this.props.actions.indexOf('delete') !== -1 &&
            this.state.formConfig !== null && hasDeleteRight;
        const showCopy = this.props.actions && this.props.actions.indexOf('copy') !== -1 &&
            this.state.formConfig !== null && hasCopyRight;

        const customColumns = Array.isArray(this.props.customColumns) ? this.props.customColumns : [];
        // let columns = [...this.state.columns, ...customColumns];
        let columns = [...this.state.columns];
        customColumns.map((r: any) => {
            // Default Filter
            if (!r.filterMethod) {
                r.filterMethod = (filter: any, row: any) => filterMethodFulltext(filter, row, r.accessor);
            }
            if (Number.isInteger(r.index)) {
                columns.splice(r.index, 0, r);
            } else {
                columns.push(r);
            }
        });
        if (this.props.disableFilter) {
            columns.forEach((c: any) => c.Filter = <div />);
        }
        // console.log(columns);

        if (typeof this.props.aggregate === 'object') {
            const aKeys = Object.keys(this.props.aggregate);
            aKeys.forEach((k: string) => {
                const idx = columns.findIndex((r: any) => { return r.accessor === k; });
                if (idx >= 0) {
                    columns[idx].aggregate = this.props.aggregate[k];
                }
            });
        }
        let dataSorted: Array<any> = this.state.filteredData ? this.state.filteredData : data;
        if (this.props.customSorter) {
            dataSorted = dataSorted.sort(this.props.customSorter);
        } else
            if (this.props.getDefaults) {
                const defaultSorter = this.props.getDefaults('customSorter', this.props.entity);
                if (defaultSorter) {
                    dataSorted = dataSorted.sort(defaultSorter);
                }
            }
        if (this.props.tableOrder) {
            const orderedColumns: Array<any> = [];
            this.props.tableOrder.forEach((item: any) => {
                const colFd = columns.find((column: any) => column.accessor === item);
                if (colFd) {
                    orderedColumns.push(colFd);
                }
            });
            columns.forEach((column: any) => {
                if (this.props.tableOrder.indexOf(column.accessor) === -1) {
                    orderedColumns.push(column);
                }
            });
            columns = orderedColumns;
        }

        // Umbenennen der Felder
        const { customFormConfig } = this.props;
        if (customFormConfig) {
            Object.keys(customFormConfig).forEach((k: string) => {
                if (customFormConfig[k].hasOwnProperty('header')) {
                    const idx = columns.findIndex((c: any) => c.accessor === k);
                    if (idx >= 0) {
                        columns[idx].HeaderText = customFormConfig[k].header;
                        columns[idx].Header = customFormConfig[k].header;
                    }
                }
            });
        }

        // WKETOOL-129: 473 - Datenexport
        // In WTable müssen Titel-Spalten ausgeschlossen werden.
        const wTableColumns = columns.filter((c: any) => c.accessor !== undefined && c.type !== 'titel');

        // console.log('WTABLE', columns,  dataSorted);
        const customActions = this.props.customActions && this.props.customActions.length > 0 ? this.props.customActions :
            (this.props.getDefaults ? this.props.getDefaults('customActions', this.props.entity) : undefined);

        return (
            <div
                className={classNames(this.props.customClass ? this.props.customClass : undefined)}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, backgroundColor: 'white' }}
            >
                {this.props.showLegend && this.state.dataForLegend &&
                    <CreateLegend
                        data={this.state.dataForLegend}
                        filterData={this.onChange}
                        delMissing={this.props.delMissing}
                        dropFilter={this.props.dropFilter}
                        onDeleteLeftoversChange={this.props.onDeleteLeftoversChange ? this.props.onDeleteLeftoversChange : () => { }}
                        showOrigRows={this.state.showOrigRows}
                        onShowOrigRowsChange={(showOrigRows) => this.setState({ showOrigRows },
                            () => this.refilter(this.state.selectedOption))}
                    />
                }
                {!this.props.showAsDataColumns ?
                    <WTable
                        containerId={this.props.containerId}
                        theme={this.props.theme}
                        module={this.props.module}
                        entity={this.props.entity}
                        tableId={this.props.tableId}
                        fixedHeader={this.props.tableFixedHeader}
                        columns={wTableColumns}
                        columnWidths={this.props.columnWidths}
                        data={dataSorted}
                        sheetName={this.props.sheetName}
                        filterable={this.props.filterable}
                        hideExport={this.props.hideExport}
                        hideFilterReset={this.props.hideFilterReset}
                        externalHeaderElements={this.getTopBarElements()}
                        sortHeaderElements={this.props.sortHeaderElements}
                        showEdit={showEdit}
                        showView={this.props.actions && this.props.actions.indexOf('view') !== -1 && this.state.formConfig !== null}
                        showDelete={showDelete}
                        showCopy={showCopy}
                        recoverBackup={this.props.actions && this.props.actions.indexOf('backup') !== -1 && this.props.enableRecover}
                        onAction={this.onAction}
                        customActions={customActions}
                        showBackup={this.props.showBackup}
                        showCompare={this.props.showCompare}
                        saveOriginal={this.props.saveOriginal}
                        dataForSaveOriginal={this.props.dataOriginalForSave}
                        rawData={this.props.rawData}
                        showPagination={this.props.tableShowPagination ? true : true}
                        pageSize={this.state.tablePageSize}
                        onShowBackup={this.props.onShowBackup ? this.props.onShowBackup : null}
                        onShowCompare={this.props.onShowCompare ? this.props.onShowCompare : null}
                        onSaveOriginal={this.props.onSaveOriginal ? this.props.onSaveOriginal : null}
                        enableRowCheckbox={this.props.enableRowCheckbox}
                        onRowSelect={this.props.onRowSelect}
                        checkedRows={this.props.checkedRows}
                        allCheckboxChecked={this.props.allCheckboxChecked}
                        isRowSelected={this.props.isRowSelected}
                        isRowSelectable={this.props.isRowSelectable}
                        forcedTableKey={this.props.forcedTableKey}
                        enableTableHistorie={this.props.enableTableHistorie}
                        enableTableStorage={this.props.enableTableStorage}
                        editCondition={this.props.editCondition}
                        showSpinner={this.state.isLoading || this.props.isLoading}
                        customRechteCheck={this.props.customRechteCheck}
                        externalRefreshIterator={this.state.externalRefreshIterator}
                        pivotBy={this.props.pivotBy}
                        expanded={this.props.expanded}
                        onRowExpand={this.props.onRowExpand}
                        defaultSorted={this.props.defaultSorted}
                        timestamp={new Date().getTime()}
                        actionsBtnReihenfolg={this.props.actionsBtnReihenfolg}
                        customClass={this.props.customClass}
                        preCfgWTableFieldGroup={this.props.preCfgWTableFieldGroup}
                        customTdProps={this.props.customTdProps}
                        onResetFilter={this.generateTableConfig}
                        exportOptions={this.props.exportOptions}
                        onFilterChange={this.props.onFilterChange}
                        showTableHelp={this.props.showTableHelp === false ? false : true}
                        showColumnsHelp={true || this.props.showColumnsHelp} // Eigenschaft default aktiviert für E-Tool
                        showColumnsSortBtn={true || this.props.showColumnsSortBtn} // Eigenschaft default aktiviert für E-Tool
                        passDataForExport={this.props.passDataForExport}
                    />
                    :
                    <WDataColumns
                        entity={this.props.entity}
                        module={this.props.module}
                        columns={columns}
                        data={dataSorted}
                        onAction={this.onAction}
                        showEdit={showEdit}
                        showView={this.props.actions && this.props.actions.indexOf('view') !== -1 && this.state.formConfig !== null}
                        showDelete={showDelete}
                        showCopy={showCopy}
                        customActions={customActions}
                        externalHeaderElements={this.getTopBarElements()}
                        customClass={this.props.customClass}
                    />
                }
            </div>
        );
    }

}
